import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { auth } from '../firebase';
import { signInWithEmailAndPassword, setPersistence, browserLocalPersistence } from 'firebase/auth';

// MUI components
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';

// Components
import { ENDPOINT_PATH } from '../config';
import { authFetch } from '../utils/authFetch';
import '../components/Login/login.css';

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [goodEmail, setGoodEmail] = useState(true);
    const [allFieldsValid, setAllFieldsValid] = useState(false);
    const [badLogin, setBadLogin] = useState(false);

    // Get redirect path from location state, default to home
    const from = location.state?.from?.pathname || '/';

    // Check if the user is already authenticated on component mount
    useEffect(() => {
        // Set persistence to LOCAL
        setPersistence(auth, browserLocalPersistence)
            .then(() => {
                const unsubscribe = auth.onAuthStateChanged((user) => {
                    console.log('User is logged in:', user ? 'Yes' : 'No');
                    
                    // If user is already logged in, redirect them
                    if (user) {
                        navigate(from, { replace: true });
                    }
                });


                // Cleanup function to unsubscribe the listener when the component unmounts
                return () => unsubscribe();
            })
            .catch((error) => {
                console.error('Error setting persistence:', error);
            });
    }, [navigate, from]);

    // Email validation
    useEffect(() => {
        if(/\S+@\S+\.\S+/.test(email.trim()) || email === '') {
            setGoodEmail(true);
        } else {
            setGoodEmail(false);
        }
    }, [email]);

    // Check if all inputs are filled in and valid
    useEffect(() => {
        setAllFieldsValid(email !== '' && goodEmail && password !== '');
    }, [email, goodEmail, password]);

    const handleLogin = async (e) => {
        e.preventDefault();

        // Trim input values
        const trimmedEmail = email.trim();
        const trimmedPassword = password.trim();
    
        console.log('Trimmed Email:', trimmedEmail);

        try {
            const userCredential = await signInWithEmailAndPassword(auth, trimmedEmail, trimmedPassword);
            const user = userCredential.user;

            // Update user in local storage
            localStorage.setItem('user', JSON.stringify(user));

            console.log('User logged in successfully:', user);
            console.log('User UID:', user.uid);

            // Redirect to the page they came from, or home
            navigate(from, { replace: true });
            
            // Reload to update NavBar state
            window.location.reload();
        } catch (error) {
            const errorCode = error.code;
            const errorMessage = error.message;

            setBadLogin(true);

            console.error('Error logging in:', errorCode, errorMessage);
        }
    };

    // Google Sign In
    const handleGoogleSignIn = async (e) => {
        e.preventDefault();
        const provider = new GoogleAuthProvider();
        const auth = getAuth();

        signInWithPopup(auth, provider)
        .then(async (userCredential) => {
            // This gives you a Google Access Token. You can use it to access the Google API.
            // Note: token and credential kept for potential future Google API integration
            // eslint-disable-next-line no-unused-vars
            const credential = GoogleAuthProvider.credentialFromResult(userCredential);
            // eslint-disable-next-line no-unused-vars
            const token = credential?.accessToken; // Available for future Google API calls
            const user = userCredential.user;

            // Update user in local storage
            localStorage.setItem('user', JSON.stringify(user));

            console.log('User logged in successfully:', user);
            console.log('User UID:', user.uid);
            
            // Create shopping cart
            try {
                // Create a shopping cart for the user
                const shoppingCartRes = await authFetch(ENDPOINT_PATH + '/api/create-shopping-cart', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        userID: user.uid
                    }),
                });
                if (shoppingCartRes.ok) {
                    console.log('Successfully created shopping cart for new user');
                } else {
                    console.error('Error creating shopping cart for new user:', shoppingCartRes.statusText);
                }
            } catch (error) {
                console.error('Error creating shopping cart for new user:', error);
            }
            
            // Redirect to the page they came from, or home
            navigate(from, { replace: true });
            
            // Reload to update NavBar state
            window.location.reload();

        // Catch google sign in error
        }).catch((error) => {
            const errorCode = error.code;
            const errorMessage = error.message;
            // Note: email and credential kept for potential future error handling/debugging
            // eslint-disable-next-line no-unused-vars
            const email = error.customData?.email;
            // eslint-disable-next-line no-unused-vars
            const credential = GoogleAuthProvider.credentialFromError(error);
            
            console.error('Google sign in error:', errorCode, errorMessage);
            setBadLogin(true);
        });
    };

    return (
        <div className="login-page-container">
            <div className="login-page-wrapper">
                <div className="login-page-content">
                    {/* Left side - Image (desktop only) */}
                    <div className="login-page-image" />
                    
                    {/* Right side - Form */}
                    <div className="login-page-form-container">
                        {/* Close button - top right of form */}
                        <button 
                            type="button"
                            className="login-page-close-button"
                            onClick={() => navigate('/')}
                            aria-label="Close"
                        >
                            <CloseIcon />
                        </button>
                        
                        <form className="login-page-form" onSubmit={handleLogin}>
                            <h2 className="login-page-title">welcome back!</h2>
                            
                            <div className="login-page-form-fields">
                                <div className="login-page-field">
                                    <TextField
                                        id="emailInput"
                                        label="email"
                                        variant="outlined"
                                        type="email"
                                        value={email}
                                        error={!goodEmail}
                                        helperText={!goodEmail ? "invalid email format" : " "}
                                        onChange={(e) => { setEmail(e.target.value); }}
                                        fullWidth
                                    />
                                </div>
                                
                                <div className="login-page-field">
                                    <TextField
                                        id="passwordInput"
                                        label="password"
                                        variant="outlined"
                                        type="password"
                                        value={password}
                                        onChange={(e) => { setPassword(e.target.value); }}
                                        fullWidth
                                    />
                                </div>
                                
                                <div className="login-page-remember">
                                    <input 
                                        type="checkbox" 
                                        className="form-check-input" 
                                        id="rememberMeCheck" 
                                    />
                                    <label className="form-check-label" htmlFor="rememberMeCheck">
                                        remember me
                                    </label>
                                </div>
                                
                                <div className="login-page-actions">
                                    {badLogin && (
                                        <div className="errorMessage login-page-error">
                                            <div>invalid login details,</div>
                                            <div>please try again</div>
                                        </div>
                                    )}
                                    
                                    <button 
                                        type="submit" 
                                        className={`btn button-dark w-100 ${!allFieldsValid && "disabled"}`}
                                        disabled={!allFieldsValid}
                                    >
                                        log in
                                    </button>
                                </div>
                                
                                <div className="login-page-google">
                                    <button 
                                        type="button" 
                                        className="google-sign-in-button w-100" 
                                        onClick={handleGoogleSignIn}
                                    >
                                        sign in with Google
                                    </button>
                                </div>
                                
                                <div className="login-page-register">
                                    <div className="login-page-register-text">don't have an account?</div>
                                    <button 
                                        type="button" 
                                        className="btn btn-danger btn-lg w-100" 
                                        onClick={() => navigate('/register')}
                                    >
                                        sign up
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;

